import { useEffect, useRef, useState } from "react";
import { QRCodeCanvas } from "qrcode.react";
import { api } from "@/lib/api";
import { Link2, Copy, Download, BarChart3 } from "lucide-react";
import { toast } from "sonner";

export default function IntakeShareCard() {
  const [sources, setSources] = useState(null);
  const qrRef = useRef(null);
  const publicUrl = `${window.location.origin}/public/intake`;

  useEffect(() => {
    api.get("/intakes/source-stats").then((r) => setSources(r.data)).catch(() => {});
  }, []);

  const copyLink = () => {
    navigator.clipboard?.writeText(publicUrl).then(
      () => toast.success("Public intake link copied"),
      () => toast.error("Could not copy link"),
    );
  };

  const downloadQr = () => {
    const canvas = qrRef.current?.querySelector("canvas");
    if (!canvas) return;
    const link = document.createElement("a");
    link.download = "intake-qr.png";
    link.href = canvas.toDataURL("image/png");
    link.click();
  };

  const maxTotal = sources?.sources?.[0]?.total || 1;

  return (
    <div className="bg-white border border-gray-200 rounded-sm shadow-sm p-6 mt-6" data-testid="intake-share-card">
      <h3 className="font-heading text-lg font-bold text-gray-900 flex items-center gap-2 mb-1.5">
        <Link2 className="w-4 h-4 text-gray-400" /> Public intake form
      </h3>
      <p className="text-sm text-gray-500 mb-5">Share this link or QR code so leads can submit an intake from anywhere.</p>

      <div className="flex flex-col sm:flex-row gap-5 items-start">
        <div ref={qrRef} className="bg-white p-3 border border-gray-200 rounded-sm shrink-0" data-testid="intake-qr">
          <QRCodeCanvas value={publicUrl} size={132} fgColor="#002FA7" level="M" includeMargin={false} />
        </div>
        <div className="flex-1 min-w-0 w-full">
          <label className="block text-xs font-bold uppercase tracking-wide text-gray-500 mb-1">Public link</label>
          <div className="flex items-center gap-2">
            <input readOnly value={publicUrl} data-testid="intake-public-url"
              className="flex-1 border border-gray-300 rounded-sm px-3 py-2 text-sm bg-gray-50 text-gray-700 focus:outline-none" />
          </div>
          <div className="flex gap-2 mt-3">
            <button onClick={copyLink} data-testid="copy-intake-link-button"
              className="flex items-center gap-1.5 bg-[#002FA7] hover:bg-[#002480] text-white px-3 py-2 rounded-sm font-semibold text-sm">
              <Copy className="w-4 h-4" /> Copy link
            </button>
            <button onClick={downloadQr} data-testid="download-qr-button"
              className="flex items-center gap-1.5 border border-gray-300 hover:bg-gray-50 text-gray-700 px-3 py-2 rounded-sm font-semibold text-sm">
              <Download className="w-4 h-4" /> Download QR
            </button>
          </div>
        </div>
      </div>

      <div className="border-t border-gray-100 mt-6 pt-5">
        <h4 className="font-heading text-sm font-bold text-gray-900 flex items-center gap-2 mb-4">
          <BarChart3 className="w-4 h-4 text-gray-400" /> Lead sources
        </h4>
        {sources?.sources?.length ? (
          <div className="space-y-3" data-testid="intake-source-stats">
            {sources.sources.map((s) => (
              <div key={s.source} data-testid={`source-row-${s.source}`}>
                <div className="flex items-center justify-between text-sm mb-1">
                  <span className="font-semibold text-gray-700 truncate">{s.source}</span>
                  <span className="text-xs text-gray-500 shrink-0 ml-2">
                    {s.converted}/{s.total} converted · <strong className="text-[#002FA7]">{s.conversion_rate}%</strong>
                  </span>
                </div>
                <div className="bg-gray-100 rounded-sm h-2.5 overflow-hidden">
                  <div className="h-full bg-[#002FA7]" style={{ width: `${(s.total / maxTotal) * 100}%` }} />
                </div>
              </div>
            ))}
          </div>
        ) : (
          <p className="text-sm text-gray-400" data-testid="intake-source-empty">No intakes yet. Source stats will appear here.</p>
        )}
      </div>
    </div>
  );
}
